import React, { useState } from "react";

import { Copy, CheckCircle } from "lucide-react";

import { useStudentClassrooms } from "../../hooks/useStudentClassrooms";

import {
  CLASSROOM_CODE_LENGTH,
  normalizeClassroomCode,
} from "../../lib/classroomEngine";

import { HOME_THEME as T } from "../../lib/homeTheme";



interface JoinClassroomPanelProps {

  studentId: string;

  studentName: string;

  compact?: boolean;

  /** Sin tarjeta; input con borde inferior, alineado a la izquierda */

  variant?: "default" | "minimal";

  onJoined?: () => void;

}



export const JoinClassroomPanel: React.FC<JoinClassroomPanelProps> = ({

  studentId,

  studentName,

  compact = false,

  variant = "default",

  onJoined,

}) => {

  const { memberships, joining, joinClassroom } = useStudentClassrooms(studentId, studentName);

  const [code, setCode] = useState("");

  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [copied, setCopied] = useState<string | null>(null);



  const isMinimal = variant === "minimal";



  const handleJoin = async () => {
    const res = await joinClassroom(code);
    setMessage({ type: res.ok ? "ok" : "err", text: res.message });
    if (res.ok) {
      setCode("");
      onJoined?.();
    }
  };

  const handleCodeChange = (raw: string) => {
    const cleaned = normalizeClassroomCode(raw).replace(/[^A-Z2-9]/g, "");
    setCode(cleaned.slice(0, CLASSROOM_CODE_LENGTH));
    if (message) setMessage(null);
  };



  const handleCopy = async (classCode: string) => {

    try {

      await navigator.clipboard.writeText(classCode);

      setCopied(classCode);

      setTimeout(() => setCopied(null), 2000);

    } catch {

      /* ignore */

    }

  };



  return (

    <div

      className={

        isMinimal

          ? "space-y-4 text-left"

          : compact

            ? "space-y-3"

            : "rounded-[20px] p-5 backdrop-blur-md"

      }

      style={

        isMinimal || compact

          ? undefined

          : { background: "rgba(36, 34, 32, 0.85)", border: `1px solid ${T.lineItem}` }

      }

    >

      {!compact && !isMinimal && (

        <div className="mb-4 flex items-center gap-2">

          <p className="text-sm font-semibold text-left" style={{ color: T.text }}>

            Unirse a clase

          </p>

        </div>

      )}



      {isMinimal && (

        <label className="block text-[10px] tracking-[0.22em] uppercase" style={{ color: T.dim }}>

          Unirse con código

        </label>

      )}



      {memberships.length > 0 && !isMinimal && (

        <ul className={`space-y-2 ${compact ? "mb-3" : "mb-4"}`}>

          {memberships.map((m) => (

            <li

              key={m.id}

              className="flex items-center justify-between rounded-xl px-3 py-2"

              style={{ background: "rgba(153,246,228,0.06)", border: `1px solid ${T.lineItem}` }}

            >

              <div className="min-w-0 text-left">

                <p className="truncate text-sm font-medium" style={{ color: T.text }}>

                  {m.classroomTitle}

                </p>

                <p className="text-[10px]" style={{ color: T.muted }}>

                  {m.teacherName || "Profesor"}

                </p>

              </div>

              <button

                type="button"

                onClick={() => handleCopy(m.classroomCode)}

                className="shrink-0 px-2 py-1 font-mono text-[10px] hover:opacity-80"

                style={{ color: T.mint }}

                title="Copiar código de clase"

              >

                {copied === m.classroomCode ? (

                  <CheckCircle className="h-3.5 w-3.5" />

                ) : (

                  m.classroomCode

                )}

              </button>

            </li>

          ))}

        </ul>

      )}



      <p className="text-xs text-left leading-relaxed" style={{ color: T.muted }}>

        {memberships.length === 0 || isMinimal

          ? "Pide a tu profesor el código de clase (8 caracteres)."

          : "¿Otra clase? Ingresa otro código:"}

      </p>



      <div
        className={
          isMinimal ? "relative z-20 flex flex-col gap-4 sm:flex-row sm:items-end" : "flex gap-2"
        }
      >
        <input
          id="join-classroom-code"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && code.length === CLASSROOM_CODE_LENGTH && !joining) {
              e.preventDefault();
              void handleJoin();
            }
          }}
          maxLength={CLASSROOM_CODE_LENGTH}
          placeholder="Código de 8 caracteres"
          aria-label="Código de clase"
          className={
            isMinimal
              ? "w-full border-0 border-b-2 bg-transparent pb-2 font-mono text-sm tracking-[0.2em] uppercase text-left focus:outline-none focus:border-[#c4b5fd]"
              : "flex-1 rounded-xl px-3 py-2.5 font-mono text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-[#c4b5fd]/40"
          }
          style={
            isMinimal
              ? { color: T.text, borderColor: T.lineItem, caretColor: T.lavender }
              : {
                  background: "rgba(0,0,0,0.35)",
                  border: `1px solid ${T.lineItem}`,
                  color: T.text,
                  caretColor: T.lavender,
                }
          }
        />

        <button

          type="button"

          onClick={handleJoin}

          disabled={joining || code.length < CLASSROOM_CODE_LENGTH}

          className={

            isMinimal

              ? "shrink-0 text-[11px] tracking-[0.14em] uppercase disabled:opacity-40 hover:text-[#99f6e4] transition-colors pb-2"

              : "rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40"

          }

          style={isMinimal ? { color: T.text } : { backgroundColor: T.tonic }}

        >

          {joining ? "…" : "Unirme"}

        </button>

      </div>



      {message && (

        <p

          className="text-xs text-left"

          style={{ color: message.type === "ok" ? T.mint : "#fb7185" }}

        >

          {message.text}

        </p>

      )}

    </div>

  );

};

